/* eslint-disable no-unused-vars */
// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, styled } from '@mui/material';
import {
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles';
import _ from 'lodash';
import { SnackbarProvider as Snackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import './App.scss';

import { FullscreenLoader, NotFound404, SwalToast } from './components/Common';
import HelpPage from './components/HelpPage/HelpPage';
import useColorPallete from './Hooks/Settings/useColorPallete';
import useCommon from './Hooks/useCommon';
import useCurrentTheme from './Theme/useCurrentTheme';
import useAuth from './Hooks/Auth/useAuth';
import { loginSuccess } from './toolkit/userSilcer';
import { initializeAxios } from './utils/Axios';
import { initializeAxiosForKeycloak } from './utils/AxiosKeyCloak';
import utils from './utils/utils';
import Layout from './views/Layout/Layout';
import Login from './views/Login/Login';
import Organization from './views/Organization/Organization';
import './wdyr';
import ClearCacheLogout from './components/ClearCacheLogout/ClearCacheLogout';
import ReadOnlyEditPipeline from './views/Pipeline/ReadOnlyEditPipeline';
import FullScreenReadOnlyEditor from './views/NewDocumentation/FullScreenReadOnlyEditor';
import Loading from './components/Common/Loading';
import ApplicationLauncher from './views/ApplicationLauncher/ApplicationLauncher';

// Check if the application launcher is not enabled or if the current path is not the root or launcher path
if (
  utils.showApplicationLauncher() !== true ||
  (window.location.pathname !== '/' &&
    window.location.pathname !== '/launcher' &&
    window.location.pathname !== '/org')
) {
  // If Keycloak login is enabled
  if (window.CONFIG.KEYCLOAK_LOGIN === 'true') {
    // Check if the REALM_ID exists in localStorage
    if (!utils.isValueEmpty(localStorage.getItem('REALM_ID'))) {
      // If the access token is missing, initialize Keycloak and set up Axios for Keycloak
      if (utils.isValueEmpty(localStorage.getItem('access_token'))) {
        utils.initKeycloak().then(() => {
          initializeAxiosForKeycloak();
        });
      } else {
        // If the access token exists, initialize Keycloak and set up Axios for Keycloak
        utils.initKeycloak();
        initializeAxiosForKeycloak();
      }
    }
  } else {
    // If Keycloak login is not enabled, initialize Axios without Keycloak
    initializeAxios();
  }
}

// eslint-disable-next-line react/prop-types
function PrivateRoute({ children, isLoggedIn, loading }) {
  const params = new URLSearchParams(window.location.search);
  const fromLauncher = params.get('fromLauncher');

  if (
    !isLoggedIn &&
    utils.showApplicationLauncher() &&
    fromLauncher !== 'true' &&
    !utils.isValueEmpty(localStorage.getItem('REALM_ID'))
  ) {
    return <Navigate to="/launcher" replace />;
  }
  if (
    !isLoggedIn &&
    window.CONFIG.KEYCLOAK_LOGIN === 'true' &&
    !utils.isValueEmpty(localStorage.getItem('REALM_ID')) &&
    loading
  ) {
    return <Loading showClearStorageBtn={true} />;
  }
  if (!isLoggedIn && utils.isValueEmpty(localStorage.getItem('REALM_ID'))) {
    return <Navigate to="/org" />;
  }

  return children;
}

const SnackbarProvider = styled(Snackbar)`
  &.SnackbarItem-contentRoot {
    width: 345px;
  }
  & .SnackbarItem-message {
    margin-right: 27px;
    word-break: break-word;
  }
  & .SnackbarItem-action {
    position: absolute;
    right: 0;
    top: 5px;
    margin-right: 5px;
    padding: 0;
  }
  &.SnackbarItem-variantSuccess {
    background: ${(p) => p.theme.palette.success.main};
  }
  &.SnackbarItem-variantError {
    background: ${(p) => p.theme.palette.error.main};
  }
  &.SnackbarItem-variantWarning {
    background: ${(p) => p.theme.palette.warning.main};
  }
  &.SnackbarItem-variantInfo {
    background: ${(p) => p.theme.palette.info.main};
  }
`;

// Resource loading error handler
const resourceErrorHandler = (event) => {
  if (
    event.target.tagName === 'SCRIPT' ||
    event.target.tagName === 'LINK' ||
    event.target.tagName === 'IMG' ||
    event.target.tagName === 'IFRAME'
  ) {
    const errorDetails = {
      type: 'Resource Loading Error',
      url: event?.target?.baseURI,
      source: event?.target?.currentSrc,
      tagName: event?.target?.tagName,
    };
    utils.handleGlobalError(errorDetails);
  }
};

const checkForCrossOrg = async () => {
  const params = new URLSearchParams(window.location.search);
  const org = params.get('org');
  const fromLauncher = params.get('fromLauncher');
  const currentOrg = localStorage.getItem('REALM_ID');
  if (org && fromLauncher === 'true') {
    if (window.CONFIG.KEYCLOAK_LOGIN === 'true') {
      await utils.checkRealmValidityAndSetRealmId(
        org,
        () => {
          if (org === currentOrg) {
            window.location = '/project/overview?fromLauncher=true';
          } else {
            // here we can prompt user if we want.
            return utils.setRealmId(org).then((data) => {
              window.location = '/project/overview?fromLauncher=true';
              if (currentOrg)
                SwalToast({
                  icon: 'info',
                  title: `Changed organization from ${currentOrg} to ${org}`,
                });
            });
          }
        },
        () => {
          console.log('Invalid organization name');
          SwalToast({
            icon: 'error',
            title: `Please provide valid organization name.`,
          });
        }
      );
    }
  } else {
    // handle the case for normal login.
  }
};

function App() {
  const theme = useCurrentTheme('light-theme');
  const { common } = useCommon();
  const { getCurrentTheme, currentSavedTheme } = useColorPallete();
  const { fetchActiveUserData } = useAuth();
  // to handle the redirection from vault to Nimbus.
  checkForCrossOrg();
  useEffect(() => {
    window.addEventListener('error', resourceErrorHandler, true);
    return () => {
      window.removeEventListener('error', resourceErrorHandler, true);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      getCurrentTheme();
    }, [500]);
  }, [common.triggerThemeChange]);
  const [applyTheme, setApplyTheme] = useState(() => {
    const newTheme = currentSavedTheme?.saved_theme;
    if (newTheme) {
      return createTheme(_.merge(theme, newTheme));
    }
    return theme;
  });

  useEffect(() => {
    const newTheme = currentSavedTheme?.saved_theme;
    if (newTheme) {
      const currentTheme = createTheme(_.merge(theme, newTheme));
      setApplyTheme(currentTheme);
    } else {
      setApplyTheme(theme);
    }
  }, [setApplyTheme, currentSavedTheme]);

  window.MS_TREE_CACHE = {};

  const { user } = useSelector((state) => state);
  const dispatch = useDispatch();
  const notistackRef = useRef();
  const [loading, setLoading] = useState(true);

  const handleKeycloakEvent = React.useCallback(
    (event) => {
      const { auth, keycloakConfig } = event.detail;
      window.KEYCLOAK_CONFIG = keycloakConfig;
      const realmId = localStorage.getItem('REALM_ID');
      fetchActiveUserData(keycloakConfig?.token, realmId)
        .then((res) => {
          dispatch(
            loginSuccess({
              isAuthenticate: auth,
              token: keycloakConfig?.token,
              refresh: keycloakConfig?.refreshToken,
              user: res?.data,
            })
          );
          setLoading(false);
          if (window.location?.pathname === '/org') {
            setTimeout(() => {
              window.location = '/project/overview';
            }, 1000);
          }
        })
        .catch(() => {
          setLoading(false);
          SwalToast({
            icon: 'error',
            title: 'Failed to fetch user details.',
          });
          if (window.location?.pathname === '/org') {
            window.location = '/project/overview';
          }
        });
    },
    [dispatch, loginSuccess, setLoading]
  );

  useEffect(() => {
    document.addEventListener('KEYCLOAK_LOGIN_SUCCESS', handleKeycloakEvent);
    if (window.CONFIG.KEYCLOAK_LOGIN !== 'true') {
      return () => {
        document.removeEventListener(
          'KEYCLOAK_LOGIN_SUCCESS',
          handleKeycloakEvent
        );
      };
    }
  }, []);

  useEffect(() => {
    try {
      const perfEntries = performance.getEntriesByType('navigation');
      if (
        localStorage.getItem('remember_me') === 'false' &&
        perfEntries[0]?.type !== 'reload'
      ) {
        localStorage.clear();
        window.location.reload();
      }
    } catch (error) {
      // Handle error
    }
  }, []);

  useEffect(() => {
    // @ts-ignore
    window.enqueueSnackbar = notistackRef.current.enqueueSnackbar;
    window.closeSnackbar = notistackRef.current.closeSnackbar;
  }, []);

  const renderNotFound = useCallback((props) => <NotFound404 {...props} />, []);

  const onClickSnackBarClose = (key) => {
    window.closeSnackbar(key);
  };
  const actionSnackbar = useCallback((key) => {
    return (
      <IconButton onClick={() => onClickSnackBarClose(key)} aria-label="Close">
        <CloseIcon sx={{ color: 'other.white' }} />
      </IconButton>
    );
  }, []);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={applyTheme}>
        <SnackbarProvider
          ref={notistackRef}
          maxSnack={3}
          hideIconVariant
          preventDuplicate
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          autoHideDuration={5000}
          action={(key) => (
            <IconButton
              onClick={() => onClickSnackBarClose(key)}
              aria-label="Close"
            >
              <CloseIcon sx={{ color: 'other.white' }} />
            </IconButton>
          )}
        >
          {/* Live region for the Snackbar */}
          <div
            id="snackbar-live-region"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              width: '0px',
              height: '0px',
              overflow: 'hidden',
            }}
          />
          <FullscreenLoader />
          <BrowserRouter>
            <Helmet>
              <title>Nimbus Uno</title>
              <meta name="Nimbus Uno" content="All solutions for your data" />
            </Helmet>
            <React.Suspense fallback="">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/org" element={<Organization />} />
                <Route path="/help" element={<HelpPage />} />
                <Route
                  path="/clear-client-storage"
                  element={<ClearCacheLogout />}
                />
                <Route
                  path="/pipeline-readonly/:id"
                  element={
                    <PrivateRoute
                      isLoggedIn={user.isAuthenticate}
                      loading={loading}
                      noLayout
                    >
                      <ReadOnlyEditPipeline />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/newdocumentation/readonly/:id"
                  element={
                    <PrivateRoute
                      isLoggedIn={user.isAuthenticate}
                      loading={loading}
                      noLayout
                    >
                      <FullScreenReadOnlyEditor />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <PrivateRoute
                      isLoggedIn={user.isAuthenticate}
                      loading={loading}
                    >
                      <Layout />
                    </PrivateRoute>
                  }
                />
                <Route path="*" name="Not Found" render={renderNotFound} />
                {utils.showApplicationLauncher() && (
                  <Route path="/launcher" element={<ApplicationLauncher />} />
                )}
              </Routes>
            </React.Suspense>
          </BrowserRouter>
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
